<script setup lang="ts">
import { Btn, BtnColor, BtnSize, BtnVariant } from '~/components/btn';
import { ProgressStepper } from './_components/progress-stepper';
import { Form } from 'vee-validate';
import {
  apiSubmitOnboardingStepData,
  BusinessOnboarding,
} from '~/api/onboarding';
import { ref } from 'vue';
import { useLoader } from '~/composables/use-loader';
import { useAccountStore } from '~/store/account';
import { useBusinessesStore } from '~/store/businesses';
import { BudgetType } from '~/api/budgets';

const props = defineProps<{
  businessOnboarding: BusinessOnboarding;
}>();

const currentStepIndex = defineModel<number>({ required: true });

const { refreshCurrentAccount } = useAccountStore();
const { nonNullableSelectedBusiness } = storeToRefs(useBusinessesStore());

const { t } = useI18n();

const templateByPath = import.meta.glob<Component>(
  './_components/templates/*/*.vue',
);

const getTemplate = (templateName: string): Component => {
  const template =
    templateByPath[
      `./_components/templates/${templateName}/${templateName}.vue`
    ];

  if (!template) {
    throw new Error(`Cannot find template ${templateName}`);
  }

  return defineAsyncComponent(template);
};

const steps = computed(() => {
  const descriptionPlural =
    nonNullableSelectedBusiness.value.tierConfig?.budgetType ===
    BudgetType.CO_FINANCED
      ? 1
      : 0;

  return props.businessOnboarding.steps.map((step) => ({
    ...step,
    title: t(`template.${step.template}.title`),
    description: t(`template.${step.template}.description`, descriptionPlural),
    component: getTemplate(step.template),
  }));
});

const currentStep = computed(() => {
  const step = steps.value.at(currentStepIndex.value);

  if (!step) {
    throw new Error(`Cannot find step for index: ${currentStepIndex.value}`);
  }

  return step;
});

const isLastStep = computed<boolean>(
  () => currentStep.value === steps.value.at(-1),
);

const formData = ref<unknown>(null);

watch(currentStep, (newVal) => {
  formData.value = newVal.data;
});

const onUpdatedFormData = (data: unknown) => {
  formData.value = data;
};

const onPreviousStepClick = () => {
  currentStepIndex.value -= 1;
};

const onSubmit = async () => {
  await useLoader({
    async action() {
      if (!currentStep.value) {
        throw new Error('Current step must be present for submitting data.');
      }

      await apiSubmitOnboardingStepData({
        onboardingStepId: Number(currentStep.value.id),
        data: formData.value,
      });
      await refreshCurrentAccount();

      currentStepIndex.value += 1;
    },
    onError(err) {
      throw err;
    },
  });
};
</script>

<template>
  <main class="max-w-[1040px] mx-auto pb-10">
    <ProgressStepper :current-step="currentStepIndex" :steps="steps" />

    <Form
      v-slot="{ meta }"
      class="flex flex-col gap-10 md:pt-10"
      @submit="onSubmit"
    >
      <div class="md:text-center px-5">
        <div class="md:hidden pb-6">
          <Btn
            :variant="BtnVariant.TEXT"
            :size="BtnSize.SM"
            :color="BtnColor.SECONDARY"
            to="/"
          >
            {{ t('exit') }}
          </Btn>
        </div>

        <h1 class="font-bold text-xl md:text-2xl">{{ currentStep.title }}</h1>

        <p class="pt-4 text-on-surface-variant">
          {{ currentStep.description }}
        </p>
      </div>

      <div>
        <Component
          :is="currentStep.component"
          :is-completed="currentStep.isCompleted"
          :config="currentStep.config"
          :initial-form-data="currentStep.data"
          @updated-form-data="onUpdatedFormData"
        />
      </div>

      <div class="flex justify-between px-4 md:px-0">
        <Btn
          :color="BtnColor.PRIMARY"
          :size="BtnSize.LG"
          @click="onPreviousStepClick"
        >
          {{ t('previous-step') }}
        </Btn>

        <div class="space-x-3">
          <span class="hidden md:inline-flex">
            <Btn
              :variant="BtnVariant.OUTLINED"
              :size="BtnSize.LG"
              :color="BtnColor.PRIMARY"
              to="/"
            >
              {{ t('complete-later') }}
            </Btn>
          </span>

          <Btn
            :variant="BtnVariant.FILLED"
            :size="BtnSize.LG"
            :color="BtnColor.PRIMARY"
            :disabled="!meta.valid && meta.touched"
            type="submit"
          >
            {{ t(isLastStep ? 'complete-onboarding' : 'next-step') }}
          </Btn>
        </div>
      </div>
    </Form>
  </main>
</template>

<i18n lang="json">
{
  "en-US": {
    "exit": "Exit",
    "previous-step": "Previous Step",
    "complete-later": "Complete Later",
    "next-step": "Next Step",
    "complete-onboarding": "Complete Onboarding",
    "template": {
      "landing-page": {
        "title": "Landing Page",
        "description": "We'll personalize your campaign landing page(s) to match your practice, so it looks and feels like you, driving patients into your practice."
      },
      "kick-off-page": {
        "title": "Schedule Kick-off",
        "description": "We'll cover the program details, help with finalizing onboarding, and answer all your questions."
      },
      "patient-demographic-page": {
        "title": "Patient Demographics",
        "description": "Tell us about the types of patients you’d like to attract so we can target your ads accordingly."
      },
      "practice-information": {
        "title": "Practice Information",
        "description": "Share your practice information so we can tailor your co-op campaigns just for you."
      },
      "co-op-funding": {
        "title": "Co-op Funding",
        "description": "Match your brand partner’s investment to launch your personalized campaign. | Take a look at your campaign summary, then click to the next step to schedule your kick-off call."
      },
      "ad-selection": {
        "title": "Ad Selection",
        "description": "Select your favorite ads—then we’ll personalize them to your practice."
      },
      "ad-authorization": {
        "title": "Authorization",
        "description": "Connect your social media accounts to allow Exfluential to run ads on your behalf."
      }
    }
  },
  "fr-FR": {},
  "es-ES": {},
  "pt-PT": {},
  "de-DE": {}
}
</i18n>
